import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useModule } from '../contexts/ModuleContext';
import { Clock, Eye, CheckCircle, Home, Globe } from 'lucide-react';
import ExpenseModal from '../components/ExpenseModal';
import { formatINR } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (amount, currency = 'INR') => {
  if (currency === 'USD') return `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'EUR') return `€${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'GBP') return `£${Number(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'OTHERS') return `${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (Other)`;
  return formatINR(amount);
};

const PendingApproval = () => {
  const { user } = useAuth();
  const { activeModule } = useModule();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpense, setSelectedExpense] = useState(null);

  const role = user?.role;
  const effectiveModule = role === 'accounts' ? 'INTERNATIONAL' : (role === 'dgm' || role === 'senior_approver') ? 'DOMESTIC' : activeModule;
  const effectiveCurrency = effectiveModule === 'DOMESTIC' ? 'INR' : 'USD';

  useEffect(() => { fetchExpenses(); }, [effectiveModule]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = { approval_status: 'pending' };
      if (['admin', 'management', 'hr', 'bdm', 'senior_approver'].includes(role)) {
        params.expense_type = effectiveModule;
      }
      const response = await axios.get(`${API}/expenses`, { params });

      // Filter based on role's approval authority
      let filtered = response.data;
      if (role === 'dgm') {
        filtered = filtered.filter(e => e.current_approval_step === 'dgm');
      } else if (role === 'senior_approver') {
        filtered = filtered.filter(e => e.current_approval_step === 'senior_approver');
      } else if (role === 'management') {
        filtered = filtered.filter(e => e.current_approval_step === 'management');
      } else if (role === 'hr') {
        filtered = filtered.filter(e => e.current_approval_step === 'hr' || e.expense_type === 'DOMESTIC');
      }
      setExpenses(filtered);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (expenseId, status, remarks) => {
    try {
      await axios.patch(`${API}/expenses/${expenseId}/status`, { status, approved_by: user.name, remarks });
      fetchExpenses();
      setSelectedExpense(null);
    } catch (error) {
      console.error('Error updating expense:', error);
      const detail = error.response?.data?.detail || 'Failed to update expense status';
      alert(detail);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    await axios.delete(`${API}/expenses/${expenseId}`);
    setSelectedExpense(null);
    fetchExpenses();
  };

  const getStepBadge = (expense) => {
    const step = expense.current_approval_step;
    const labels = { dgm: 'DGM Review', senior_approver: 'Sr. Approver Review', management: 'Management Review', hr: 'HR Final', accounts: 'Accounts Review' };
    const colors = { dgm: 'bg-violet-50 text-violet-700', senior_approver: 'bg-orange-50 text-orange-700', management: 'bg-blue-50 text-blue-700', hr: 'bg-amber-50 text-amber-700', accounts: 'bg-emerald-50 text-emerald-700' };
    return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${colors[step] || 'bg-gray-100 text-gray-600'}`}>{labels[step] || step}</span>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading...</div></div>;
  }

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {effectiveModule === 'DOMESTIC' ? <Home className="w-5 h-5 text-indigo-500" /> : <Globe className="w-5 h-5 text-emerald-500" />}
          Pending Approval
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {effectiveModule === 'DOMESTIC' ? 'Domestic expenses awaiting your approval' : 'International expenses awaiting review'}
        </p>
      </div>

      <div className="card mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
            <Clock className="w-5 h-5 text-amber-600" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-900">{expenses.length}</div>
            <div className="text-xs text-gray-500">
              {role === 'dgm' ? 'Awaiting your DGM approval' :
               role === 'senior_approver' ? 'Awaiting your Senior Approver review' :
               role === 'management' ? 'Awaiting Management approval' :
               role === 'accounts' ? 'International expenses to review' :
               'Requests awaiting approval'}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Employee</th>
                <th>Brand</th>
                <th>Category</th>
                <th>Vendor</th>
                <th>Amount</th>
                <th>Step</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-8">
                    <CheckCircle className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No pending requests for your role</p>
                  </td>
                </tr>
              ) : (
                expenses.map((expense) => (
                  <tr key={expense.id} data-testid={`expense-row-${expense.id}`}>
                    <td className="whitespace-nowrap">
                      {new Date(expense.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      {(() => {
                        const days = Math.floor((Date.now() - new Date(expense.created_at).getTime()) / 86400000);
                        if (days > 7) return <span className="ml-1 text-[10px] px-1 py-0.5 bg-red-100 text-red-700 rounded font-medium">{'>'} 7d</span>;
                        if (days > 3) return <span className="ml-1 text-[10px] px-1 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">{'>'} 3d</span>;
                        return null;
                      })()}
                    </td>
                    <td className="font-medium">{expense.employee_name}</td>
                    <td>
                      <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                        {expense.brand_division || 'N/A'}
                      </span>
                    </td>
                    <td>{expense.category}</td>
                    <td>
                      {expense.vendor}
                      {expense.duplicate_warning && <span className="ml-1 text-[10px] px-1 py-0.5 bg-amber-100 text-amber-700 rounded">DUP</span>}
                    </td>
                    <td className="font-semibold">{formatCurrency(expense.amount, expense.currency || 'INR')}</td>
                    <td>{getStepBadge(expense)}</td>
                    <td>
                      <button onClick={() => setSelectedExpense(expense)}
                        className="text-indigo-600 hover:text-indigo-800 font-medium text-xs flex items-center gap-1"
                        data-testid={`review-expense-${expense.id}`}>
                        <Eye className="w-3.5 h-3.5" />Review
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedExpense && (
        <ExpenseModal expense={selectedExpense} onClose={() => setSelectedExpense(null)}
          onApprove={handleApproval}
          onDelete={role === 'admin' ? handleDeleteExpense : undefined}
          showActions={true} actionType="approval"
        />
      )}
    </div>
  );
};

export default PendingApproval;
